import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Security.css';
import '../ScrollAnimations.css';

function Security() {
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, observerOptions);

    document.querySelectorAll('.fade-in-up, .stagger-item').forEach(el => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const securityFeatures = [
    {
      icon: "🔐",
      title: "Multi-Factor Authentication",
      description: "All management portal accounts protected with MFA. Even if a password is compromised, your data stays secure."
    },
    {
      icon: "🔒",
      title: "Data Encryption",
      description: "All data encrypted in transit (TLS 1.3) and at rest. Your employees' wellbeing data is protected at every stage."
    },
    {
      icon: "👁️",
      title: "Privacy by Design",
      description: "Individual responses are never visible to employers. Only aggregated, anonymised insights are shared."
    },
    {
      icon: "👥",
      title: "Role-Based Access",
      description: "Granular permissions ensure staff only see what they need. Admins, managers, and employees have appropriate access levels."
    },
    {
      icon: "📋",
      title: "Audit Logging",
      description: "Every access and action is logged for accountability. Full audit trail available for compliance requirements."
    },
    {
      icon: "🔄",
      title: "Rotating Access Codes",
      description: "Employee app access codes rotate monthly, ensuring only current staff can access the platform."
    }
  ];

  const complianceItems = [
    {
      title: "GDPR Compliant",
      description: "Full compliance with UK GDPR and Data Protection Act 2018. Clear data processing agreements, right to erasure, and data portability."
    },
    {
      title: "ISO 27001 Aligned",
      description: "Security controls and policies aligned with ISO 27001 standards. Working towards formal certification."
    },
    {
      title: "Regular Security Reviews",
      description: "Ongoing security assessments and improvements. Incident response procedures tested and documented."
    },
    {
      title: "UK Data Residency",
      description: "All data stored and processed within UK/EU data centres. No data transferred outside approved jurisdictions."
    }
  ];

  return (
    <div className="security-page">
      {/* Hero Section */}
      <section className="security-hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-icon">🛡️</div>
            <h1>Security & Trust</h1>
            <p className="hero-tagline">Your employees' wellbeing data deserves the highest protection</p>
            <p className="hero-lead">Here's how we keep it safe</p>
          </div>
        </div>
      </section>

      {/* Security Promise */}
      <section className="security-promise">
        <div className="container">
          <div className="promise-card fade-in-up">
            <h2>Our Security Promise</h2>
            <p>
              At F-IT Score, security isn't an afterthought - it's built into everything we do. 
              We understand that wellbeing data is sensitive, and we treat it with the respect it deserves.
            </p>
            <p className="highlight">
              <strong>Individual employee responses are never visible to employers</strong> - 
              only anonymised, aggregated insights that help improve workplace wellbeing.
            </p>
          </div>
        </div>
      </section>

      {/* Security Features */}
      <section className="security-features">
        <div className="container">
          <h2 className="section-title fade-in-up">Security Features</h2>
          <div className="features-grid">
            {securityFeatures.map((feature, index) => (
              <div key={index} className="feature-card fade-in-up">
                <div className="feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
                <span className="status-badge">✓ Active</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Privacy by Design - Highlighted */}
      <section className="privacy-highlight">
        <div className="container">
          <div className="privacy-card fade-in-up">
            <div className="privacy-icon">👁️</div>
            <div className="privacy-content">
              <h2>Privacy by Design</h2>
              <p>Our architecture ensures employee privacy is protected at its core:</p>
              <ul>
                <li>✓ Individual responses are <strong>never visible</strong> to employers</li>
                <li>✓ Management only sees <strong>aggregated, anonymised</strong> data</li>
                <li>✓ Minimum group sizes prevent identification of individuals</li>
                <li>✓ Employees control what personal data they share</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance */}
      <section className="compliance-section">
        <div className="container">
          <h2 className="section-title fade-in-up">Compliance & Standards</h2>
          <div className="compliance-grid">
            {complianceItems.map((item, index) => (
              <div key={index} className="compliance-card fade-in-up">
                <div className="compliance-check">✓</div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Infrastructure */}
      <section className="infrastructure-section">
        <div className="container">
          <h2 className="section-title fade-in-up">Infrastructure Security</h2>
          <div className="infrastructure-grid">
            <div className="infrastructure-card fade-in-up">
              <div className="infra-icon">🌍</div>
              <h3>UK/EU Hosting</h3>
              <p>Data stored in secure UK/EU data centres</p>
            </div>
            <div className="infrastructure-card fade-in-up">
              <div className="infra-icon">⏰</div>
              <h3>24/7 Monitoring</h3>
              <p>Continuous security monitoring and alerts</p>
            </div>
            <div className="infrastructure-card fade-in-up">
              <div className="infra-icon">🚨</div>
              <h3>Incident Response</h3>
              <p>Documented procedures for rapid response</p>
            </div>
          </div>
        </div>
      </section>

      {/* Documentation */}
      <section className="documentation-section">
        <div className="container">
          <div className="documentation-card fade-in-up">
            <h2>Security Documentation Available</h2>
            <p>We maintain comprehensive security documentation. Available upon request for enterprise clients:</p>
            <div className="doc-list">
              <span>✓ Information Security Policy</span>
              <span>✓ Privacy Policy & GDPR Procedures</span>
              <span>✓ Incident Response Plan</span>
              <span>✓ Business Continuity Plan</span>
              <span>✓ Data Retention Policy</span>
              <span>✓ Vulnerability Management Policy</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="security-cta">
        <div className="container">
          <div className="cta-card fade-in-up">
            <h2>Questions About Security?</h2>
            <p>We're happy to discuss our security measures, complete security questionnaires, or provide documentation for your procurement process.</p>
            <div className="cta-buttons">
              <a href="mailto:info@p2bs.org" className="btn btn-white">Contact Us</a>
              <a href="tel:+441636550190" className="btn btn-outline-white">Call: 01636 550190</a>
            </div>
          </div>
        </div>
      </section>

      {/* Back to services */}
      <section className="back-section">
        <div className="container">
          <Link to="/" className="back-link">← Back to Home</Link>
        </div>
      </section>
    </div>
  );
}

export default Security;
